import {
  ApiOkSchema,
  BarcodeLookupSchema,
  BarcodeOperationSchema,
  BootstrapSchema,
  FulfillmentQueueSchema,
  InventorySchema,
  StatusSchema,
  type ApiOkPayload,
  type BarcodeLookupPayload,
  type BarcodeOperationPayload,
  type BootstrapPayload,
  type FulfillmentQueuePayload,
  type InventoryPayload,
  type StatusPayload
} from "@glasshalf/contracts";

type RequestOptions = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
};

export class ApiError extends Error {
  status: number;
  code: string;
  payload: unknown;

  constructor(status: number, code: string, message: string, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(path, {
    method: options.method || (options.body === undefined ? "GET" : "POST"),
    credentials: "same-origin",
    signal: options.signal,
    headers: options.body === undefined ? undefined : { "Content-Type": "application/json" },
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });
  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    const record = typeof payload === "object" && payload ? payload as Record<string, unknown> : {};
    throw new ApiError(
      response.status,
      String(record.error || "request_failed"),
      String(record.message || response.statusText || "요청 처리에 실패했습니다."),
      payload
    );
  }
  return payload as T;
}

export const api = {
  status: async (): Promise<StatusPayload> => StatusSchema.parse(await request("/api/status")),
  bootstrap: async (): Promise<BootstrapPayload> => BootstrapSchema.parse(await request("/api/bootstrap")),
  inventory: async (): Promise<InventoryPayload> => InventorySchema.parse(await request("/api/inventory")),
  fulfillmentQueue: async (): Promise<FulfillmentQueuePayload> => FulfillmentQueueSchema.parse(await request("/api/fulfillment/queue")),
  login: async (loginId: string, password: string): Promise<ApiOkPayload> => ApiOkSchema.parse(await request("/api/login", { body: { loginId, password } })),
  setup: async (payload: { setupToken?: string; loginId: string; password: string }): Promise<ApiOkPayload> => ApiOkSchema.parse(await request("/api/setup", { body: payload })),
  logout: async (): Promise<ApiOkPayload> => ApiOkSchema.parse(await request("/api/logout", { method: "POST" })),
  syncOrders: async (): Promise<ApiOkPayload> => ApiOkSchema.parse(await request("/api/cafe24/sync/orders", { method: "POST", body: { source: "orders-page-auto" } })),
  createOrderCase: async (payload: Record<string, unknown>): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/orders/cases", { body: payload }));
  },
  syncProducts: async (): Promise<ApiOkPayload> => ApiOkSchema.parse(await request("/api/cafe24/sync/products", { method: "POST", body: { source: "products-detail" } })),
  createProduct: async (payload: Record<string, unknown>): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/inventory/products", { body: payload }));
  },
  updateProduct: async (productKey: string, payload: Record<string, unknown>): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request(`/api/inventory/products/${encodeURIComponent(productKey)}`, { method: "PATCH", body: payload }));
  },
  deleteProduct: async (productKey: string, payload: { reason: string }): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request(`/api/inventory/products/${encodeURIComponent(productKey)}`, { method: "DELETE", body: payload }));
  },
  registerProductOnChannel: async (productKey: string, payload: { channelCode?: string; reason?: string }): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request(`/api/inventory/products/${encodeURIComponent(productKey)}/channel-registration`, { body: payload }));
  },
  matchChannelProduct: async (productKey: string, payload: { channelProductId: number; colorwayCode: string; reason?: string }): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request(`/api/inventory/products/${encodeURIComponent(productKey)}/channel-product-match`, { body: payload }));
  },
  receive: async (payload: { toLocationCode: string; note?: string; lines: Array<{ sku: string; quantity: number }> }): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/inventory/receive-matrix", { body: payload }));
  },
  moveInventory: async (payload: { sku: string; quantity: number; fromLocationCode?: string; toLocationCode?: string; type?: string; note?: string }): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/inventory/move", { body: payload }));
  },
  lookupBarcode: async (barcode: string): Promise<BarcodeLookupPayload> => {
    return BarcodeLookupSchema.parse(await request(`/api/inventory/barcodes/${encodeURIComponent(barcode)}`));
  },
  runBarcodeOperation: async (payload: {
    barcode: string;
    operation: "receive" | "move" | "issue";
    quantity?: number;
    locationCode?: string;
    fromLocationCode?: string;
    toLocationCode?: string;
    reason?: string;
    note?: string;
    dryRun?: boolean;
  }): Promise<BarcodeOperationPayload> => {
    return BarcodeOperationSchema.parse(await request("/api/inventory/barcode-operations", { body: payload }));
  },
  saveAllocations: async (payload: { channelCode: string; locationCode: string; allocations: Array<{ sku: string; quantity: number }> }): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/inventory/channel-sku-allocations", { body: payload }));
  },
  readChannelStock: async (payload: { channelCode: string; productKey?: string; alignInventory?: boolean }): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/inventory/channel-sync-online", { body: payload }));
  },
  pushChannelStock: async (payload: { channelCode: string; productKey?: string }): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/inventory/channel-stock-sync-requests", { body: payload }));
  },
  fulfillmentStep: async (step: "pick" | "pack" | "ship", payload: { channelId: number; orderKey: string; lineKey: string; reason?: string; trackingNo?: string }): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request(`/api/fulfillment/${step}`, { body: payload }));
  },
  saveLocation: async (payload: Record<string, unknown>): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/settings/inventory-locations", { body: payload }));
  },
  saveAccount: async (payload: Record<string, unknown>): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/settings/accounts", { body: payload }));
  },
  archivePosts: async (): Promise<Record<string, unknown>> => {
    return request("/api/archive/posts");
  },
  saveOnlineMall: async (payload: Record<string, unknown>): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/online-mall-apis", { body: payload }));
  },
  startCafe24OAuth: async (): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/cafe24/oauth/start", { method: "POST", body: { source: "settings" } }));
  },
  disconnectCafe24: async (payload: { reason?: string } = {}): Promise<ApiOkPayload> => {
    return ApiOkSchema.parse(await request("/api/cafe24/disconnect", { method: "POST", body: payload }));
  }
};
